import React, { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { PASSWORD_RULES_TEXT, validatePasswordAgainstPolicy } from '@shared/passwordPolicy';

const client = generateClient({ authMode: 'userPool' });

interface SetMemberPasswordDialogProps {
  /** The member's email on record (the Cognito sign-in). */
  email: string;
  /** Shown in the title, e.g. "Pat Angler". */
  displayName: string;
  onClose: () => void;
  /** Called after a successful set, with the message to show. */
  onDone: (message: string) => void;
}

/**
 * #136: the member types a new permanent password on the admin's device.
 * The password lives only in this component's state, is cleared on success
 * and on close, and is never logged, stored or put in a URL.
 */
const SetMemberPasswordDialog: React.FC<SetMemberPasswordDialogProps> = ({ email, displayName, onClose, onDone }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const clearAndClose = () => {
    setPassword('');
    setConfirm('');
    setError(null);
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) clearAndClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const check = validatePasswordAgainstPolicy(password);
    if (!check.ok) {
      setError(check.message);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await (client.mutations as any).setMemberPassword({ email, newPassword: password });
      const d = parseLambdaResponse(res?.data);
      if (d?.success) {
        setPassword('');
        setConfirm('');
        onDone(d.message || 'Password set. The member can sign in now.');
        return;
      }
      setError(d?.error || 'Failed to set the password. Please try again.');
    } catch {
      setError('Failed to set the password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputType = show ? 'text' : 'password';
  const inputClass =
    'w-full min-h-[44px] rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="set-password-title"
        aria-describedby="set-password-instructions"
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h3 id="set-password-title" className="text-lg font-bold text-gray-900">
          Set a new password for {displayName}
        </h3>
        <p id="set-password-instructions" className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-sm text-blue-900">
          Hand this device to the member. They type their new password; you don't need to see it.
        </p>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3" noValidate>
          <div>
            <label htmlFor="set-password-new" className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <input
              ref={firstFieldRef}
              id="set-password-new"
              type={inputType}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="set-password-rules"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="set-password-confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm new password
            </label>
            <input
              id="set-password-confirm"
              type={inputType}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
            />
          </div>
          <p id="set-password-rules" className="text-sm text-gray-600">
            {PASSWORD_RULES_TEXT}
          </p>
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300"
            />
            Show password
          </label>
          {error && (
            <p role="alert" className="text-sm font-medium text-red-700">
              {error}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={clearAndClose}
              disabled={submitting}
              className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
            >
              {submitting ? 'Setting password…' : 'Set password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetMemberPasswordDialog;
